import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import FAQAccordion from '../Services/FAQAccordion';
import Seo from '../Seo';

const SolutionBPO = () => {


  const faqs = [
    {
      q: "What is Business Process Outsourcing (BPO) and how does it work?",
      a: "Business Process Outsourcing (BPO) is a strategic operating model where an organization delegates selected business functions to a specialized service partner. Instead of treating outsourcing as a cost-only decision, we position it as a performance and scalability lever aligned to measurable outcomes. Libsys manages process design, staffing, governance, and delivery controls so quality and speed improve simultaneously. This helps internal teams focus on strategic initiatives while operational workflows continue with clear SLA accountability. Over time, BPO also creates standardization, reporting transparency, and stronger process resilience."
    },
    {
      q: "What are the benefits of using BPO services?",
      a: "The most immediate benefit is improved operational efficiency through process specialization, standardized workflows, and dedicated delivery teams. BPO also reduces management overhead by shifting execution governance and staffing complexity to a structured partner model. Organizations gain access to trained talent, technology accelerators, and performance benchmarking that are difficult to build internally at the same pace. Cost optimization becomes more predictable because outcomes are managed against agreed service levels and productivity targets. Most importantly, leadership teams recover strategic bandwidth by moving repetitive non-core execution outside critical decision paths."
    },
    {
      q: "What types of services are typically outsourced in the BPO industry?",
      a: "Commonly outsourced domains include customer support, technical help desk, finance and accounting operations, HR process support, and back-office data workflows. Many organizations also outsource high-volume document processing, quality assurance support, and selected digital operations based on business priorities. The right scope is determined by process maturity, compliance requirements, and expected business impact rather than volume alone. Libsys typically starts with structured process assessment to identify functions that can be improved fastest with externalized delivery. This phased approach enables stable transition, measurable outcomes, and controlled scaling over time."
    },
    {
      q: "What is temporary staffing and how can it benefit my business?",
      a: "Temporary staffing allows businesses to fill short-term roles or niche positions quickly. It provides flexibility to handle seasonal peaks or project-based needs without long-term commitment."
    },
    {
      q: "What are the differences between BPO and temporary staffing services?",
      a: "BPO and temporary staffing differ in ownership, accountability, and operating model. In BPO, the provider is accountable for end-to-end process delivery against defined SLAs, governance standards, and output metrics. In temporary staffing, professionals are placed into your existing management structure, and day-to-day delivery remains internally governed. BPO is ideal when you want process transformation and sustained operating efficiency at scale. Temporary staffing is more suitable when you need quick capacity augmentation within an existing process framework."
    },
    {
      q: "How do I choose the right BPO service provider?",
      a: "Start by evaluating proven delivery capability in your domain, including process transition experience and measurable client outcomes. Assess governance maturity, reporting transparency, escalation management, and the provider's ability to commit to SLA-driven execution. Security, compliance, and business continuity practices should be validated early, especially for customer or financial workflows. You should also review talent quality, training model, and technology enablement depth to ensure long-term scalability. The strongest partners combine operational rigor, transformation mindset, and collaborative stakeholder engagement."
    },
    {
      q: "What services do temporary staffing agencies offer?",
      a: "They offer recruitment, screening, payroll management, and placement for short-term, contract-to-hire, or project-specific roles across various skill levels."
    },
    {
      q: "What are the best practices for managing a relationship with a BPO provider?",
      a: "Successful partnerships begin with clear scope definition, outcome KPIs, and governance cadence agreed at transition stage. Regular performance reviews should include not only SLA status, but also root-cause trends, quality indicators, and improvement opportunities. Joint planning sessions help align process enhancements with business priorities and seasonal workload changes. Communication should remain transparent across operations, compliance, and leadership stakeholders to reduce execution gaps. Treating the provider as a strategic partner rather than a transactional vendor consistently delivers better long-term outcomes."
    }
  ];

  return (
    <div className="bg-white min-h-screen pt-4 [&_.text-h1]:text-h2 [&_.text-h2]:text-h3 [&_.text-body]:text-small [&_.text-small]:text-subtext">
      <Seo
        title="Business Process Outsourcing"
        description="Libsys BPO solutions — SLA-driven back-office operations, domain-trained delivery teams, and continuous improvement programs that let you focus on core growth."
        path="/solutions/bpo"
      />
      {/* ══ HERO SECTION ══════════════════════════════════════════════════════ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-6">
        <div className="rounded-[2.5rem] border border-[#236086]/10 bg-slate-50/70 p-6 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 text-red-tagline mb-5">
                Business Process Outsourcing
              </span>
              <h1 className="text-h1 text-primary mb-6">
                Outsource <span className="text-secondary">Smartly</span> with Libsys
              </h1>
              <p className="text-slate-600 text-body leading-[1.8] font-medium mb-8 max-w-2xl">
                Operational efficiency is the platform upon which enterprise growth is built. Libsys BPO services move far beyond traditional back-office support, providing a strategic engine for process optimization and digital transformation. We integrate deep domain expertise with intelligent automation to manage your critical business functions with higher precision and lower overhead. Whether it is finance, procurement, or customer operations, our delivery model focuses on measurable outcomes and continuous improvement so your internal teams can remain focused on your core competitive mission.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 mb-10">
                {[
                  'SLA-managed service delivery',
                  'Scalable 24/7 support teams',
                  'Rigorous QA & Audit controls',
                  'Outcome-focused delivery models',
                  'Tier-1 Technical assistance',
                  'Real-time performance reporting'
                ].map((item) => (
                  <li key={item} className="text-slate-700 text-small font-bold leading-relaxed flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary mt-[7px] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:+16307991556"
                  className="inline-flex items-center gap-3 px-10 py-5 bg-primary hover:bg-secondary text-white font-black text-[11px] uppercase tracking-widest rounded-xl transition-all shadow-xl active:scale-95 group"
                >
                  Schedule Solution Review
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="lg:col-span-7 lg:-mt-12">
              <div className="rounded-[2.5rem] overflow-hidden border border-slate-200 shadow-[0_30px_70px_-15px_rgba(35,96,134,0.2)] bg-slate-100 relative group">
                <div className="absolute inset-0 bg-primary/5 group-hover:bg-transparent transition-colors z-10 pointer-events-none" />
                <img src="/hero_tech_office.png" alt="Operational Excellence Simulation" className="w-full h-[400px] lg:h-[600px] object-cover group-hover:scale-105 transition-transform duration-1000" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ STRATEGIC ADVANTAGES SECTION ══════════════════════════════════════ */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-h2 text-primary mb-6">Built for Operational Resilience</h2>
            <p className="text-slate-600 font-medium leading-relaxed">
              We provide the structural stability and talent density required to manage complex workflows, allowing your leadership to reclaim focus on core innovation.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: "Scale Velocity", desc: "Instantly adjust delivery capacity to handle volume volatility without the administrative burden of internal scaling." },
              { title: "Domain Expertise", desc: "Gain immediate access to pre-screened, trained professionals with specific expertise in customer care, tech support, and data operations." },
              { title: "Process Maturity", desc: "We implement standardized, documented workflows that eliminate single-points-of-failure and ensure delivery continuity." }
            ].map((adv, i) => (
              <div key={i} className="p-10 rounded-[2rem] bg-[#e2edf5] border border-[#236086]/10 hover:shadow-xl transition-all">
                <h4 className="text-xl font-black text-primary mb-4">{adv.title}</h4>
                <p className="text-slate-600 text-body leading-relaxed font-medium">{adv.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CORE VALUE ══════════════════════════════════════════════════════ */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-red-tagline block mb-4">Operational Excellence</span>
              <h2 className="text-h2 text-primary mb-6">Attain Your Organizational Objectives</h2>
              <p className="text-slate-600 text-small mb-8 leading-[1.8] font-medium">
                Our BPO methodology is rooted in the philosophy of continuous optimization. We don't just migrate existing processes; we transform them to be faster, leaner, and more responsive to your market needs. By leveraging real-time performance analytics and a high-governance delivery framework, we ensure that every interaction reflects your brand's standards of excellence.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 mb-10">
                {[
                  { title: "Skilled Talent", desc: "Expert workforce pools trained in specific enterprise workflows." },
                  { title: "Rapid Elasticity", desc: "Instantly adjust delivery capacity to handle volume volatility." },
                  { title: "Data Governance", desc: "Full transparency through SLA-backed reporting & dashboarding." },
                  { title: "Modern Delivery", desc: "Seamless integration of AI and automation for increased precision." }
                ].map((item, i) => (
                  <div key={i} className="space-y-2.5">
                    <div className="text-secondary flex items-center gap-2">
                      <span className="font-black text-primary text-tiny uppercase tracking-widest">{item.title}</span>
                    </div>
                    <p className="text-slate-600 text-subtext leading-relaxed font-semibold">{item.desc}</p>
                  </div>
                ))}
              </div>
              <ul className="space-y-4">
                {[
                  'Customized transition roadmaps for zero-downtime migration',
                  'Proactive root-cause analysis to reduce process errors',
                  'Dedicated Quality Assurance leads assigned to every workflow'
                ].map((item) => (
                  <li key={item} className="text-slate-800 text-subtext font-bold leading-relaxed flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
              <div className="bg-white rounded-[2.5rem] p-6 lg:p-10 border border-[#236086]/10 shadow-2xl shadow-slate-200/50">
                <h3 className="text-xl font-black text-primary mb-10 text-center uppercase tracking-widest">Operational Domains</h3>
                <div className="grid grid-cols-1 gap-5">
                   {[
                    { title: "Customer Success", desc: "Omnichannel 24/7 engagement models designed for loyalty.", bg: "bg-blue-50", color: "text-blue-600" },
                    { title: "Technical Support", desc: "Tier 1-3 expert resolution for complex product ecosystems.", bg: "bg-emerald-50", color: "text-emerald-600" },
                    { title: "Back-Office Ops", desc: "High-volume document and data processing with 99.9% accuracy.", bg: "bg-violet-50", color: "text-violet-600" }
                  ].map((domain, i) => (
                    <div key={i} className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100 flex items-center gap-6 hover:bg-white hover:shadow-lg transition-all group">
                      <div>
                        <h4 className="font-black text-primary text-lg mb-1">{domain.title}</h4>
                        <p className="text-subtext text-slate-500 font-bold leading-relaxed">{domain.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ CTA SECTION ═══════════════════════════════════════════════════════ */}
      <section className="py-12 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="bg-primary rounded-[3rem] p-8 lg:py-10 lg:px-12 relative overflow-hidden text-center lg:text-left">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/10 rounded-full blur-2xl -ml-20 -mb-20 pointer-events-none" />
          
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <span className="text-blue-100/60 text-tiny font-black uppercase tracking-[0.4em] mb-2 block">High-Impact Outsourcing</span>
              <h2 className="text-3xl lg:text-4xl font-black text-white mb-4 leading-tight">
                Accelerate Your Growth with <span className="text-secondary">Libsys BPO</span>
              </h2>
              <p className="text-blue-100/80 text-lg mb-6 max-w-2xl font-medium">
                From high-volume logistics to complex financial reconciliation, we provide the governed capacity you need to scale without unmanaged overhead.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                <a
                  href="tel:+16307991556"
                  className="px-10 py-5 bg-white text-primary font-black rounded-xl hover:bg-blue-50 transition-all flex items-center gap-3 uppercase tracking-widest text-subtext shadow-2xl active:scale-95"
                >
                  630-799-1556
                  <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="grid grid-cols-1 gap-3">
                {[
                  { k: 'Efficiency', v: '99.9% Process Accuracy' },
                  { k: 'Scale', v: 'Global Delivery Model' },
                  { k: 'Focus', v: 'Zero-Overhead Transition' }
                ].map((item) => (
                  <div key={item.k} className="bg-white/10 backdrop-blur-md border border-white/10 p-4 rounded-2xl text-left">
                    <p className="text-blue-100/50 text-[10px] font-black uppercase tracking-widest">{item.k}</p>
                    <p className="text-white text-small font-bold mt-1">{item.v}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQAccordion faqs={faqs} />
    </div>
  );
};

export default SolutionBPO;
